import { Injectable } from '@angular/core';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {PAGE, WORD_HOMONYM_API_URL} from '../../defines/protocol';
import {SearchParam} from '../../defines/client.const';
import {Homonym} from '../../models/homonym.model';
import {ListLoader} from '../../interfaces/list-loader';
import {ApiService} from '../api/api.service';
import {NormalizeSearchKeyword} from '../../defines/client.func';


/**
 * HomonymService: 이운이의어(異韻異義語) 데이터 관리 서비스
 * 이운이의어: 같은 글자, 다른 소리, 다른 뜻 (예: 行 - 행/항/항)
 *
 * 알려진 제약사항:
 * - API가 페이지네이션 방식으로 데이터를 제공하여, 전체 개수는 첫 번째 API 응답 후에만 알 수 있음
 * - totalCount는 GetNextList() 첫 호출 시 data.count로 초기화됨 (line 122 참조)
 */
@Injectable({
  providedIn: 'root'
})
export class HomonymService implements ListLoader<Homonym> {
  protected urlApi = WORD_HOMONYM_API_URL;

  protected totalCount: number | null = null
  private listHomonym: Array<Homonym> | null = null;
  private mapHomonym: Map<string, Homonym> | null = null;
  private nextUrl: string | null = null;
  private load$: Observable<boolean> | null = null;
  private loadDetail$: Observable<Homonym> | null = null;

  constructor(
    private api: ApiService,
  ) { }

  /******************************************************************************
   * ListLoader<Homonym> 구현부
   *****************************************************************************/
  public getList(): Array<Homonym> | null {
    return this.listHomonym;
  }
  public getCurCount(): number | null {
    return this.listHomonym?.length ?? null;
  }
  public getTotalCount(): number | null {
    return this.totalCount;
  }
  public IsComplete(): boolean {
    return this.totalCount === 0 ||
      (this.totalCount !== null && this.listHomonym?.length === this.totalCount);
  }
  public IsLoading(): boolean {
    return this.load$ !== null;
  }
  public getById(id: string): Homonym | null {
    return this.mapHomonym?.get(id) ?? null;
  }

  public ResetList(param?: SearchParam): void {
    this.nextUrl = null;
    this.listHomonym = null;
    this.mapHomonym = null;
    this.totalCount = null;

    if (param) {
      if ('id' in param) {
        this.nextUrl = this.urlApi + '?id=' + param.id;
      }
      else if ('index' in param) {
        this.nextUrl = this.urlApi + '?index=' + param.index;
      }
      else if ('keyword' in param) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        if (keyword) {
          this.nextUrl = this.urlApi + '?search=' + keyword;
        }
      }

      if ('level' in param) {
        this.nextUrl = this.nextUrl ?
          this.nextUrl + '&level=' + param.level :
          this.urlApi + '?level=' + param.level;
      }

      if ('start' in param) {
        this.nextUrl = this.nextUrl ?
          this.nextUrl + '&start=' + param.start :
          this.urlApi + '?start=' + param.start;
      }
    }

    if (!this.nextUrl) {
      this.nextUrl = this.urlApi;
    }

    console.log('HomonymService::ResetList()', param, this);
  }

  public GetNextList(): Observable<boolean> {
    console.log('HomonymService::GetNextList()');

    if (this.nextUrl === null) {
      return throwError({error: 'No more pages in HomonymList'});
    }

    if (this.load$) {
      return this.load$;
    }

    if (this.nextUrl === null) {
      throw new Error('HomonymService::GetNextList() nextUrl is null');
    }
    return this.load$ = this.api.Get(this.nextUrl).pipe(
      catchError(err => {
        if ('status' in err && err.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      }),
      map((data: PAGE) => {
        console.log('getWordList.api.Get data =', data);

        let first = false;
        if (this.totalCount === null) {
          this.totalCount = data.count;
          this.listHomonym = new Array<Homonym>();
          this.mapHomonym = new Map<string, Homonym>();
          first = true;
        }
        else if (this.totalCount !== data.count) {
          console.error('[error]' + this.urlApi, data );
        }

        data.results.forEach(value => {
          if (this.listHomonym === null || this.mapHomonym === null) {
            throw new Error('listHomonym and mapHomonym must be initialized');
          }
          const homonym = new Homonym(value);
          this.listHomonym.push(homonym);
          this.mapHomonym.set(homonym.name, homonym);
        });

        this.nextUrl = data.next;

        return first;
      })
    );
  }


  /******************************************************************************
   * 상세 이운이의어 정보 획득
   * 위쪽의 목록에는 간섭이 없도록 처리하는 것이 좋다.
   * TODO: 로딩중인 것을 처리. (예외상황 처리 추가해야 함)
   *****************************************************************************/
  public GetDetail(homonym: string): Observable<Homonym> {
    const url = this.urlApi + homonym + '/';
    console.log('HomonymService::GetDetail() url =', url);

    return this.loadDetail$ = this.api.Get(url).pipe(
      finalize(() => this.loadDetail$ = null),
      map(value => {
        const out = new Homonym(value);
        console.log('HomonymService::GetDetail data = ', value, out);
        return out;
      })
    );
  }
}
